import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Accordion } from 'components/molecules/Accordion';
import { LicencesSearchForm } from 'pages/LegalEntitiesPage/LicencesSearchForm';
import { Loader } from 'components/atoms/Loader';
import { Table } from 'components/molecules/Table';
import { CardList } from 'components/molecules/CardList';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { parseDictionary } from 'helpers/dictionary';
import {
  renderLicenceDataColor,
  renderRowColor,
  renderRowColorHover,
} from 'components/molecules/Table/helpers';
import {
  legalEntityLicencesTableColumns,
  LicenceAttachmentsDetailTableColumns,
  mapLELicencesToDataSource,
} from 'pages/LegalEntitiesPage/helpers';
import { LegalEntityLicenceTableCard } from '../../LegalEntitiesLicenceTableCard';
import { ISearchLegalEntityLicencesFormValues } from 'models/form';
import { ERouteLinks } from 'models/route';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { getGlobalPropertyByKey } from 'requests/globalProperty';
import { downloadUrlAsFile } from 'utils/files';
import { LicenceActionModal } from '../../LegalEntityViewLicencePage/LicenceActionModal';
import { FormModal } from 'components/molecules/Modal';
import { colors } from 'theme/colors';
import { IDictionary } from 'models/dictionary';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import {
  getLicenceCaseRenewStatusText,
  getLicenceCaseVaryStatusText,
} from 'pages/CaseManagementPage/CasesPage/helpers';
import { ECaseRenewalStatus, ECaseVariationStatus } from 'models/cases';

interface ILegalEntityLicencesSectionProps {
  showLicences: boolean;
  id: string;
  type: string;
  isAllExpanded: boolean;
  t: (key: string) => string;
  licenceAuthorityValid: boolean;
  isDesktop: boolean;
  weaponLicenceStatus: IDictionary[] | null;
  attachmentDocumentTypeData: IDictionary[] | null;
  shortDateFormat: string;
  scrollPosition: React.MutableRefObject<number>;
  legalEntity: ILegalEntityData | null | undefined;
  orgLegalEntity: IOrgLegalEntityData | null | undefined;
  image: any;
}

const LegalEntityLicencesSection = ({
  showLicences,
  id,
  type,
  isAllExpanded,
  t,
  licenceAuthorityValid,
  isDesktop,
  weaponLicenceStatus,
  attachmentDocumentTypeData,
  shortDateFormat,
  scrollPosition,
  legalEntity,
  orgLegalEntity,
  image,
}: ILegalEntityLicencesSectionProps) => {
  const navigate = useNavigate();

  const {
    getLegalEntityLicencesData,
    legalEntityLicences,
    paginatorLicences,
    getLicenceAttachmentFile,
  } = useLegalEntities();
  const {
    getCaseRenewalStatus,
    caseRenewalStatus,
    getCaseVariationStatus,
    caseVariationStatus,
  } = useCase();
  const [leftDaysNotify, setLeftDaysNotify] = useState<number>(0);
  const [licenceQuery, setLicenceQuery] = useState<
    Partial<ISearchLegalEntityLicencesFormValues>
  >({});
  const [currentPageLicences, setCurrentPageLicences] = useState<number>(1);
  const [perPageLicences, setPerPageLicences] = useState<EPerPages>(EPerPages.perPage25);
  const [licencesSort, setLicencesSort] = useState<ISortOptions | null>(null);
  const [isLicencePaginationLoading, setIsLicencePaginationLoading] = useState(false);
  const [isLicenceQueryLoading, setIsLicenceQueryLoading] = useState<boolean>(false);
  const [isLegalEntityLicencesLoading, setIsLegalEntityLicencesLoading] =
    useState<boolean>(false);
  const [isLicencesSortLoaded, setIsLicencesSortLoaded] = useState(true);
  const [licenceOpenDetail, setLicenceOpenDetail] = useState<string>('');
  const [isRenewModalOpen, setIsRenewModalOpen] = useState<boolean>(false);
  const [isVaryModalOpen, setIsVaryModalOpen] = useState<boolean>(false);
  const [selectedLicenceId, setSelectedLicenceId] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [missingFieldsModalOpen, setMissingFieldsModalOpen] = useState(false);

  const previousLicencesSortRef = useRef(licencesSort);

  useEffect(() => {
    (async function getLeftDaysInit() {
      const leftDays = await getGlobalPropertyByKey(
        'licence_expiration_notification_days_before',
      );
      if (leftDays) setLeftDaysNotify(Number(leftDays.value));
    })();
  }, []);

  useEffect(() => {
    (async function getLegalEntityLicencesInit() {
      if ((!!legalEntity || !!orgLegalEntity) && id && showLicences) {
        if (!legalEntityLicences.length) setIsLegalEntityLicencesLoading(true);
        if (licencesSort !== previousLicencesSortRef.current) setIsLicencesSortLoaded(false);
        setIsLicencePaginationLoading(true);
        await getLegalEntityLicencesData(
          currentPageLicences,
          perPageLicences,
          {
            legalUniqueId: id,
            weaponLicenceNumber: licenceQuery.weaponLicenceNumber ?? null,
            statusUniqueId: licenceQuery.statusUniqueId ?? null,
          },
          licencesSort,
        );
        setIsLicenceQueryLoading(false);
        setIsLegalEntityLicencesLoading(false);
        setIsLicencePaginationLoading(false);
        setIsLicencesSortLoaded(true);
        previousLicencesSortRef.current = licencesSort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageLicences,
    perPageLicences,
    licencesSort,
    legalEntity,
    orgLegalEntity,
    licenceQuery,
    showLicences,
  ]);

  const handleView = (licenceId: string) =>
    navigate(
      `${ERouteLinks.LegalEntityViewLicence.replace(':id', id).replace(':licenceId', licenceId)}?type=${type}&expand=licences`,
    );

  const varyHandle = (licenceId: string) => {
    setSelectedLicenceId(licenceId);
    getCaseVariationStatus(licenceId).then(() => setIsVaryModalOpen(true));
  };

  const renewHandle = (licenceId: string) => {
    setSelectedLicenceId(licenceId);
    getCaseRenewalStatus(licenceId).then(() => setIsRenewModalOpen(true));
  };

  const handleAttachmentView = async (licenceId: string, attachmentId: string) => {
    try {
      const url = await getLicenceAttachmentFile(licenceId, attachmentId, 'view');
      if (url) window.open(url, '_blank');
    } catch (err) {
      console.error('Błąd otwierania pliku', err);
    }
  };

  const handleAttachmentDownload = async (
    licenceId: string,
    attachmentId: string,
    name: string,
  ) => {
    try {
      const url = await getLicenceAttachmentFile(licenceId, attachmentId, 'download');
      if (url) downloadUrlAsFile(url, name);
    } catch (err) {
      console.error('Błąd otwierania pliku', err);
    }
  };

  if (!showLicences) return null;

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Accordion
          title={t('legalEntityDetails.licences.accordion.label')}
          name="licences"
          expanded={isAllExpanded}
        >
          <LicencesSearchForm
            onSubmit={(it) => {
              setIsLicenceQueryLoading(true);
              setLicenceQuery(it);
            }}
            isSearchButtonDisabled={isLicenceQueryLoading}
          />
          {isLegalEntityLicencesLoading ? (
            <Loader isLoading={isLegalEntityLicencesLoading} />
          ) : (
            <div>
              {isDesktop ? (
                <Table
                  columns={legalEntityLicencesTableColumns({
                    handleView,
                    handleRenew: renewHandle,
                    handleAmend: varyHandle,
                    t,
                    leftDaysNotify,
                    setLicenceOpenDetail,
                    licenceOpenDetail,
                    dateFormat: shortDateFormat,
                    renderLicenceStatusFieldColor: (data: ITableDataSource) =>
                      renderLicenceDataColor(data).stateField,
                    licenceAuthorityValid,
                  })}
                  dataSource={mapLELicencesToDataSource(
                    legalEntityLicences,
                    parseDictionary(weaponLicenceStatus, 'name'),
                    parseDictionary(attachmentDocumentTypeData, 'name'),
                    id,
                  )}
                  detailId={licenceOpenDetail}
                  detailsColumns={LicenceAttachmentsDetailTableColumns({
                    handleView: handleAttachmentView,
                    handleDownload: handleAttachmentDownload,
                    dateFormat: shortDateFormat,
                  })}
                  onSort={(it) => {
                    setLicencesSort(it);
                    scrollPosition.current = window.scrollY;
                  }}
                  specificRowColor={renderRowColor}
                  specificRowColorHover={renderRowColorHover}
                  isSortLoaded={isLicencesSortLoaded}
                />
              ) : (
                <CardList
                  items={mapLELicencesToDataSource(
                    legalEntityLicences,
                    parseDictionary(weaponLicenceStatus, 'name'),
                    parseDictionary(attachmentDocumentTypeData, 'name'),
                    id,
                  )}
                  render={(data, index) => (
                    <Box key={index} sx={{ mb: 2, mt: 2 }}>
                      <LegalEntityLicenceTableCard
                        handleView={handleView}
                        handleRenew={renewHandle}
                        handleAmend={varyHandle}
                        leftDaysNotify={leftDaysNotify}
                        data={data}
                        key={index}
                        licenceAuthorityValid={licenceAuthorityValid}
                      />
                    </Box>
                  )}
                />
              )}
            </div>
          )}
          {paginatorLicences && (
            <Pagination
              count={paginatorLicences.totalElements}
              perPage={perPageLicences}
              onChangePage={(page) => setCurrentPageLicences(page)}
              onChangePerPage={(value) => setPerPageLicences(value)}
              current={currentPageLicences}
              isLoading={isLicencePaginationLoading}
            />
          )}
        </Accordion>
      </Grid>

      <LicenceActionModal
        open={isRenewModalOpen}
        title={t('renewLicence.modal.title')}
        message={t(getLicenceCaseRenewStatusText(caseRenewalStatus?.status))}
        approveButtonLabel={t('renewLicence.modal.button.approve.label')}
        cancelButtonLabel={t('general.no.label')}
        onClickApprove={() => {
          if (
            selectedLicenceId &&
            (caseRenewalStatus?.status === ECaseRenewalStatus.EXISTS_DENIED ||
              caseRenewalStatus?.status === ECaseRenewalStatus.NOT_EXISTS)
          ) {
            navigate(
              `${ERouteLinks.LegalEntityLicenceRenewFromLE.replace(':id', id).replace(
                ':licenceId',
                selectedLicenceId as string,
              )}?type=${type === ELegalEntityTypes.individual ? LEAccordionNames.INDIVIDUAL : LEAccordionNames.ORGANISATION}&expand=licences`,
            );
          } else if (caseRenewalStatus?.caseUniqueId) {
            navigate(
              `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':id', id as string).replace(':caseId', caseRenewalStatus?.caseUniqueId as string)}?type=${type === ELegalEntityTypes.individual ? LEAccordionNames.INDIVIDUAL : LEAccordionNames.ORGANISATION} `,
            );
          }
          setIsRenewModalOpen(false);
          setSelectedLicenceId(null);
        }}
        onClickClose={() => {
          setIsRenewModalOpen(false);
          setSelectedLicenceId(null);
        }}
      />
      <LicenceActionModal
        open={isVaryModalOpen}
        title={t('varyLicence.modal.title')}
        message={t(getLicenceCaseVaryStatusText(caseVariationStatus?.status))}
        approveButtonLabel={t('renewLicence.modal.button.approve.label')}
        cancelButtonLabel={t('general.no.label')}
        onClickApprove={() => {
          if (
            selectedLicenceId &&
            (caseVariationStatus?.status === ECaseVariationStatus.EXISTS_DENIED ||
              caseVariationStatus?.status === ECaseVariationStatus.NOT_EXISTS)
          ) {
            const entity = legalEntity || orgLegalEntity;
            if (entity) {
              navigate(
                `${ERouteLinks.LegalEntityLicenceVaryFromLE.replace(':id', entity.uniqueId).replace(':licenceId', selectedLicenceId)}?type=${
                  type === ELegalEntityTypes.individual
                    ? LEAccordionNames.INDIVIDUAL
                    : LEAccordionNames.ORGANISATION
                }&expand=licences`,
              );
            }
          } else if (caseVariationStatus?.caseUniqueId) {
            navigate(
              `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':id', id as string).replace(':caseId', caseVariationStatus?.caseUniqueId as string)}?type=${
                type === ELegalEntityTypes.individual
                  ? LEAccordionNames.INDIVIDUAL
                  : LEAccordionNames.ORGANISATION
              }`,
            );
          }
          setIsVaryModalOpen(false);
          setSelectedLicenceId(null);
        }}
        onClickClose={() => {
          setIsVaryModalOpen(false);
          setSelectedLicenceId(null);
        }}
      />

      <FormModal
        open={missingFieldsModalOpen}
        onClickClose={() => setMissingFieldsModalOpen(false)}
        cancelButtonLabel={t('general.close.label')}
      >
        <Typography variant="h5" sx={{ color: colors.primary500 }}>
          {t('legalEntity.missingData.header')}
        </Typography>
        <Stack sx={{ gap: '24px' }}>
          <Typography variant="body2">{t('legalEntity.missingData.body1')}</Typography>
          <Typography variant="body2">
            <ul style={{ listStyleType: 'initial', marginLeft: '24px' }}>
              {missingFields.map((field, index) => (
                <li key={index}>{t(field)}</li>
              ))}
            </ul>
          </Typography>
          <Typography variant="body2">{t('legalEntity.missingData.body2')}</Typography>
        </Stack>
      </FormModal>
    </>
  );
};

export default LegalEntityLicencesSection;
